import { useState, useEffect } from 'react';
import useSlingApi from '@sdk/api/useSlingApi';
import useToast from '@sdk/common/toast/useToast';
import { extendBillingInfo } from '@sdk/account/utils';
import getErrorForToast from '@sdk/api/utils/getErrorForToast';
import gettext from '@sdk/lang/gettext';
import { SUBSCRIPTION_NAMES } from '@sdk/account/constants';

import { useBillingInfo } from '@web/account/billing/BillingInfoContext';

export const usePauseSubscriptionModal = ({
  accept,
  isVisible,
  subscriptionLevel,
  fromCancel,
}: {
  accept: () => void;
  isVisible: boolean;
  subscriptionLevel: number;
  fromCancel: boolean;
}) => {
  const { billingInfo, onBillingInfoUpdated } = useBillingInfo();
  const [resumeDate, setResumeDate] = useState(billingInfo.scheduledToResumeDate);

  const [askForReason, setAskForReason] = useState(false);
  const [selectedReasonId, setSelectedReasonId] = useState<number>();
  const [selectedReasonText, setSelectedReasonText] = useState<string>();
  const [reasonRequiresText, setReasonRequiresText] = useState(false);

  const { showErrorToast } = useToast();
  const api = useSlingApi();

  useEffect(() => {
    if (isVisible) {
      setResumeDate(billingInfo.scheduledToResumeDate);
      setAskForReason(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible]);

  const acceptPauseSubscription = async (selectedReasonId, selectedReasonText) => {
    try {
      const { data } = billingInfo.scheduledToResumeDate
        ? await api.account.updatePauseSubscription(resumeDate)
        : await api.account.pauseSubscription(resumeDate, selectedReasonId, selectedReasonText);
      onBillingInfoUpdated(extendBillingInfo(data));
      accept();
    } catch (e) {
      if (e.response.status === 400) {
        // error in date, move back one step
        setAskForReason(false);
      }

      showErrorToast(getErrorForToast(e));
      throw e;
    }
  };

  const description = fromCancel
    ? gettext(
        'Instead of canceling your %(subscriptionName)s subscription, you can pause it until a later date, so you automatically regain access to paid features at the resume date.',
        { subscriptionName: SUBSCRIPTION_NAMES[subscriptionLevel] }
      )
    : gettext(
        'If you don’t intend to use Sling for a while, you can pause your subscription until later. Your subscription will resume automatically on the specified date. '
      );

  return {
    acceptPauseSubscription,
    resumeDate,
    setResumeDate,
    askForReason,
    setAskForReason,
    selectedReasonId,
    setSelectedReasonId,
    selectedReasonText,
    setSelectedReasonText,
    reasonRequiresText,
    setReasonRequiresText,
    description,
  };
};
